"use client";

import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Eye, EyeOff, ArrowRight, Check, Zap, Globe, ChevronDown, Sparkles, X as XIcon, Clock, Lock } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLang } from "@/context/lang";
import type { Lang } from "@/context/lang";
import { Logo } from "@/components/logo";
import { useTheme } from "@/components/theme-provider";
import { track } from "@/lib/analytics";

type TierKey = "free" | "starter" | "creator" | "studio" | "pro";

interface TierTheme {
  cardBg: string;
  cardBgSelected: string;
  badge: string;
  priceGrad: string;
  ribbon: string;
  ribbonGlow: string;
  ringSelected: string;
  zap: string;
}

const TIER_THEMES: Record<TierKey, TierTheme> = {
  free: {
    cardBg: "bg-slate-50/70 dark:bg-white/5",
    cardBgSelected: "bg-emerald-50 dark:bg-emerald-500/10",
    badge: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30",
    priceGrad: "from-emerald-500 to-teal-500 dark:from-emerald-300 dark:to-teal-300",
    ribbon: "from-emerald-400 to-teal-500",
    ribbonGlow: "shadow-emerald-500/40",
    ringSelected: "ring-emerald-400 dark:ring-emerald-400",
    zap: "text-emerald-500 dark:text-emerald-400",
  },
  starter: {
    cardBg: "bg-slate-50/70 dark:bg-white/5",
    cardBgSelected: "bg-slate-100 dark:bg-white/10",
    badge: "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-700",
    priceGrad: "from-slate-700 to-slate-900 dark:from-slate-100 dark:to-white",
    ribbon: "from-slate-400 to-slate-600",
    ribbonGlow: "shadow-slate-400/30",
    ringSelected: "ring-slate-400 dark:ring-slate-300",
    zap: "text-slate-500 dark:text-slate-400",
  },
  creator: {
    cardBg: "bg-gradient-to-b from-cyan-50/70 to-white dark:from-cyan-950/40 dark:to-white/5",
    cardBgSelected: "bg-gradient-to-b from-cyan-100 to-cyan-50 dark:from-cyan-900/50 dark:to-cyan-950/30",
    badge: "bg-cyan-100 text-cyan-700 ring-1 ring-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-300 dark:ring-cyan-500/30",
    priceGrad: "from-cyan-500 to-teal-500 dark:from-cyan-300 dark:to-teal-300",
    ribbon: "from-cyan-400 to-teal-500",
    ribbonGlow: "shadow-cyan-500/40",
    ringSelected: "ring-cyan-400",
    zap: "text-cyan-500 dark:text-cyan-400",
  },
  studio: {
    cardBg: "bg-gradient-to-b from-blue-50/70 to-white dark:from-blue-950/40 dark:to-white/5",
    cardBgSelected: "bg-gradient-to-b from-blue-100 to-blue-50 dark:from-blue-900/50 dark:to-blue-950/30",
    badge: "bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30",
    priceGrad: "from-blue-500 to-indigo-600 dark:from-blue-300 dark:to-indigo-300",
    ribbon: "from-blue-400 to-indigo-500",
    ribbonGlow: "shadow-blue-500/40",
    ringSelected: "ring-blue-400",
    zap: "text-blue-500 dark:text-blue-400",
  },
  pro: {
    cardBg: "bg-gradient-to-b from-purple-50/70 to-white dark:from-purple-950/40 dark:to-white/5",
    cardBgSelected: "bg-gradient-to-b from-purple-100 to-purple-50 dark:from-purple-900/50 dark:to-purple-950/30",
    badge: "bg-purple-100 text-purple-700 ring-1 ring-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:ring-purple-500/30",
    priceGrad: "from-purple-500 to-fuchsia-600 dark:from-purple-300 dark:to-fuchsia-300",
    ribbon: "from-purple-400 to-fuchsia-500",
    ribbonGlow: "shadow-purple-500/40",
    ringSelected: "ring-purple-400",
    zap: "text-purple-500 dark:text-purple-400",
  },
};

interface PlanItem {
  id: string;
  tier: TierKey;
  label: string;
  name: string;
  price: number;
  original?: number;
  credits: number;
  audio?: string;
  features: string[];
  disabled?: string[];
  ribbon?: string;
  showZap?: boolean;
}

const PLANS_HARDCODED: PlanItem[] = [
  {
    id: "free_trial", tier: "free", label: "FREE TRIAL", name: "Free Trial",
    price: 0, credits: 10000, audio: "~10 min of audio",
    features: ["All features included", "3-day trial (Full access)", "No credit card required"],
    ribbon: "TRY FREE",
  },
  {
    id: "starter", tier: "starter", label: "STARTER", name: "Starter",
    price: 5, original: 9, credits: 60000, audio: "~60 min of audio",
    features: ["Single voice generation", "2,600+ AI voices", "25+ languages", "No 5,000-character limit", "MP3 128kbps export", "Commercial license"],
    disabled: ["Dialogue mode", "Voice cloning"],
  },
  {
    id: "creator", tier: "creator", label: "CREATOR", name: "Creator",
    price: 25, original: 40, credits: 200000, audio: "~200 min of audio",
    features: ["Everything in Starter", "Dialogue mode", "Multi-language", "1 voice clone", "Voice effects", "Emotional controls", "Creaneo AI assistant", "Edit panel (all tools)", "MP3 192kbps HD", "No character limit ∞", "Credit rollover (2 months)"],
    ribbon: "MOST POPULAR", showZap: true,
  },
  {
    id: "studio", tier: "studio", label: "STUDIO", name: "Studio",
    price: 59, original: 89, credits: 500000, audio: "~8 hours of audio",
    features: ["Everything in Creator", "Video dubbing", "3 voice clones", "Multi-track editor", "Background music mixer", "WAV export", "Priority queue", "Credit rollover (2 months)"],
    ribbon: "NEW",
  },
  {
    id: "pro", tier: "pro", label: "PRO", name: "Pro",
    price: 129, original: 199, credits: 1000000, audio: "~16 hours of audio",
    features: ["Everything in Studio", "10 voice clones", "PCM 44.1kHz studio quality", "Webhook integration", "Priority support", "Credit rollover (2 months)"],
  },
];

const LANGS: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "tr", label: "Türkçe" },
  { code: "es", label: "Español" },
  { code: "bs", label: "Bosanski" },
  { code: "ru", label: "Русский" },
  { code: "de", label: "Deutsch" },
];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [funnelStartFired, setFunnelStartFired] = useState(false);
  const fireStart = () => {
    if (!funnelStartFired) {
      setFunnelStartFired(true);
      track("register_form_start");
    }
  };
  const [selectedPlan, setSelectedPlan] = useState("free_trial");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const router = useRouter();
  const { T, lang, setLang } = useLang();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const isDark = mounted && resolvedTheme === "dark";
  const [error, setError] = useState("");

  const plans = PLANS_HARDCODED;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (step === 1) {
      if (!acceptTerms || !acceptPrivacy) {
        setError(T("auth.must_accept"));
        return;
      }
      setStep(2);
      return;
    }

    // Step 2: Register + redirect to Stripe checkout
    setLoading(true);
    track("register_form_submit", { label: selectedPlan });
    try {
      const resp = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password, plan: selectedPlan }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        localStorage.setItem("speakroar-user", JSON.stringify(data.user));
        track("register_success", { label: selectedPlan, metadata: { user_id: data.user?.id } });

        // Google Ads conversion event
        if (typeof window !== "undefined" && typeof (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag === "function") {
          (window as unknown as { gtag: (...args: unknown[]) => void }).gtag("event", "ads_conversion_Abone_olma_1", {});
        }

        // Free trial - go directly to dashboard
        if (selectedPlan === "free_trial") {
          router.push("/dashboard");
          return;
        }

        // Paid plan - redirect to Stripe checkout
        const payResp = await fetch("/api/stripe/create-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_id: selectedPlan, email }),
        });
        const payData = await payResp.json();

        if (payResp.ok && payData.url && payData.url.startsWith("https://")) {
          window.location.href = payData.url;
        } else {
          router.push("/dashboard");
        }
      } else {
        const reason = data.error || `http_${resp.status}`;
        track("register_error", { label: reason, metadata: { status: resp.status, code: data?.code } });
        setError(data.error || "Registration failed");
        setLoading(false);
      }
    } catch (e) {
      track("register_error", { label: "network_error", metadata: { msg: String(e).slice(0, 200) } });
      setError(T("auth.connection_error"));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-cyan-50/40 via-white to-cyan-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex items-center justify-center px-4 py-12">
      {/* Ambient animated background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <motion.div
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-cyan-300/30 dark:bg-cyan-500/10 blur-[120px]"
          animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-blue-300/25 dark:bg-blue-500/10 blur-[120px]"
          animate={{ x: [0, -25, 0], y: [0, 20, 0], scale: [1, 0.95, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-40 left-1/4 w-[480px] h-[480px] rounded-full bg-purple-300/20 dark:bg-purple-500/10 blur-[120px]"
          animate={{ x: [0, 20, 0], y: [0, -15, 0], scale: [1, 1.05, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`w-full relative transition-all duration-500 ${step === 2 ? "max-w-5xl" : "max-w-lg"}`}
      >
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex justify-center mb-6">
            <Logo size="lg" white={isDark} />
          </Link>
          <h1 className="text-3xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 dark:from-cyan-300 dark:via-sky-400 dark:to-purple-400 bg-clip-text text-transparent">
              {T("auth.register")}
            </span>
          </h1>

          {/* Steps indicator - 2 steps with animated progress line */}
          <div className="flex items-center justify-center mt-7">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <motion.div
                  initial={false}
                  animate={{
                    scale: step === s ? 1.12 : 1,
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 18 }}
                  className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-black transition-colors ${
                    step >= s
                      ? "bg-gradient-to-br from-cyan-500 to-teal-500 text-white shadow-lg shadow-cyan-500/40"
                      : "bg-white dark:bg-white/5 text-neutral-400 dark:text-slate-500 ring-1 ring-neutral-200 dark:ring-white/10"
                  }`}
                >
                  {step > s ? <Check size={15} strokeWidth={3} /> : s}
                </motion.div>
                {s < 2 && (
                  <div className="relative mx-2 w-14 h-0.5 bg-neutral-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: step > s ? "100%" : "0%" }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-14 mt-2.5">
            <span className={`text-[11px] font-semibold ${step === 1 ? "text-cyan-600 dark:text-cyan-300" : "text-neutral-400 dark:text-slate-500"}`}>{T("auth.step_account")}</span>
            <span className={`text-[11px] font-semibold ${step === 2 ? "text-cyan-600 dark:text-cyan-300" : "text-neutral-400 dark:text-slate-500"}`}>{T("auth.step_plan")}</span>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-white/5 backdrop-blur-xl rounded-3xl ring-1 ring-neutral-200/70 dark:ring-white/10 shadow-2xl shadow-cyan-500/10 dark:shadow-black/30 p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Account */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-neutral-700 dark:text-slate-300 mb-2 tracking-wide uppercase">{T("auth.name")}</label>
                  <input type="text" value={name} onChange={(e) => { fireStart(); setName(e.target.value); }} className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all" placeholder="John Doe" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-neutral-700 dark:text-slate-300 mb-2 tracking-wide uppercase">{T("auth.email")}</label>
                  <input type="email" value={email} onChange={(e) => { fireStart(); setEmail(e.target.value); }} className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all" placeholder="you@example.com" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-neutral-700 dark:text-slate-300 mb-2 tracking-wide uppercase">{T("auth.password")}</label>
                  <div className="relative">
                    <input type={showPass ? "text" : "password"} value={password} onChange={(e) => { fireStart(); setPassword(e.target.value); }} className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all pr-10" placeholder="Min 8 characters" required minLength={8} />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-cyan-600 dark:text-slate-500 dark:hover:text-cyan-300 transition-colors">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Legal Agreements */}
                <div className="space-y-3 pt-2">
                  <label className={`flex items-start gap-3 cursor-pointer group ${!acceptTerms ? "text-neutral-600 dark:text-slate-300" : "text-neutral-900 dark:text-white"}`}>
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-neutral-300 dark:border-white/20 text-cyan-500 focus:ring-cyan-500/20 cursor-pointer accent-cyan-500"
                    />
                    <span className="text-xs leading-relaxed">
                      <Link href="/terms" target="_blank" className="text-cyan-600 font-semibold hover:underline">{T("auth.terms_link")}</Link>
                      {", "}
                      <Link href="/privacy" target="_blank" className="text-cyan-600 font-semibold hover:underline">{T("auth.privacy_link")}</Link>
                      {", "}
                      {T("auth.accept_terms_suffix")}
                    </span>
                  </label>

                  <label className={`flex items-start gap-3 cursor-pointer group ${!acceptPrivacy ? "text-neutral-600 dark:text-slate-300" : "text-neutral-900 dark:text-white"}`}>
                    <input
                      type="checkbox"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-neutral-300 dark:border-white/20 text-cyan-500 focus:ring-cyan-500/20 cursor-pointer accent-cyan-500"
                    />
                    <span className="text-xs leading-relaxed">
                      <Link href="/ccpa" target="_blank" className="text-cyan-600 font-semibold hover:underline">{T("auth.ccpa_link")}</Link>
                      {" "}
                      {T("auth.accept_privacy_suffix")}
                    </span>
                  </label>

                  <label className={`flex items-start gap-3 cursor-pointer group ${!acceptMarketing ? "text-neutral-600 dark:text-slate-300" : "text-neutral-900 dark:text-white"}`}>
                    <input
                      type="checkbox"
                      checked={acceptMarketing}
                      onChange={(e) => setAcceptMarketing(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-neutral-300 dark:border-white/20 text-cyan-500 focus:ring-cyan-500/20 cursor-pointer accent-cyan-500"
                    />
                    <span className="text-xs leading-relaxed">
                      I agree to receive promotional emails and marketing communications from SpeakRoar (CreateNano LLC). You can unsubscribe at any time.
                    </span>
                  </label>
                </div>
              </motion.div>
            )}

            {/* Step 2: Plan Selection */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <p className="text-sm text-neutral-600 dark:text-slate-300 mb-5 font-medium text-center">{T("auth.choose_pack")}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {plans.map((plan) => {
                    const t = TIER_THEMES[plan.tier];
                    const isSelected = selectedPlan === plan.id;
                    return (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative pt-3 cursor-pointer transition-all ${isSelected ? "scale-[1.02]" : "hover:-translate-y-0.5"}`}
                      >
                        {plan.ribbon && (
                          <div className={`absolute -top-0.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-t-md rounded-b-sm bg-gradient-to-r ${t.ribbon} text-[9px] font-black text-white tracking-widest shadow ${t.ribbonGlow} z-10 whitespace-nowrap`}>
                            {plan.ribbon}
                          </div>
                        )}
                        <div className={`relative p-4 rounded-2xl overflow-hidden h-full ring-1 transition-all ${
                          isSelected
                            ? `ring-2 ${t.ringSelected} ${t.cardBgSelected} shadow-lg`
                            : `ring-neutral-200/70 dark:ring-white/10 ${t.cardBg}`
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${t.badge} text-[9px] font-black tracking-widest`}>
                              {plan.label}
                            </span>
                            {isSelected && (
                              <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${t.ribbon} flex items-center justify-center shadow ${t.ribbonGlow}`}>
                                <Check size={11} className="text-white" strokeWidth={3} />
                              </div>
                            )}
                          </div>

                          {plan.original != null && (
                            <div className="text-[11px] text-neutral-400 dark:text-slate-500 line-through font-semibold">${plan.original}</div>
                          )}
                          <div className="flex items-baseline gap-1 mb-1">
                            <span className={`text-3xl font-black bg-gradient-to-r ${t.priceGrad} bg-clip-text text-transparent leading-none`}>
                              {plan.price === 0 ? "$0" : `$${plan.price}`}
                            </span>
                            <span className="text-xs text-neutral-400 dark:text-slate-500 font-medium">/mo</span>
                          </div>

                          <div className="flex items-center gap-1.5 mb-0.5 text-xs">
                            <Zap size={12} className={t.zap} fill="currentColor" />
                            <span className="font-bold text-neutral-900 dark:text-white">
                              {plan.credits >= 1000000 ? `${(plan.credits/1000000).toFixed(0)}M` : `${(plan.credits/1000).toFixed(0)}K`} credits/mo
                            </span>
                          </div>
                          {plan.audio && (
                            <div className="text-[10px] text-neutral-400 dark:text-slate-500 font-medium mb-2 ml-4">{plan.audio}</div>
                          )}

                          {plan.tier === "free" && (
                            <div className="mb-2 mt-1 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/60 dark:border-emerald-500/20 flex items-center gap-1.5">
                              <Clock size={10} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300">3-day trial · Full access</span>
                            </div>
                          )}
                          {plan.tier === "free" && (
                            <div className="mb-2 flex items-center gap-1.5">
                              <Lock size={10} className="text-neutral-400 dark:text-slate-500 flex-shrink-0" />
                              <span className="text-[10px] text-neutral-500 dark:text-slate-400 font-medium">No credit card required</span>
                            </div>
                          )}

                          <ul className="space-y-1.5">
                            {plan.features.filter(f => plan.tier !== "free" || !["3-day trial (Full access)", "No credit card required"].includes(f)).map((f) => (
                              <li key={f} className="flex items-start gap-1.5 text-[11px] text-neutral-700 dark:text-white/85 font-medium leading-tight">
                                <Check size={11} className={`${t.zap} mt-0.5 flex-shrink-0`} strokeWidth={3} />
                                {f}
                              </li>
                            ))}
                            {plan.disabled?.map((f) => (
                              <li key={f} className="flex items-start gap-1.5 text-[11px] text-neutral-400 dark:text-slate-600 font-medium leading-tight line-through">
                                <XIcon size={11} className="text-neutral-300 dark:text-slate-700 mt-0.5 flex-shrink-0" strokeWidth={3} />
                                {f}
                              </li>
                            ))}
                          </ul>
                          {plan.showZap && (
                            <Sparkles size={12} className={`absolute top-2 right-2 ${t.zap} opacity-50`} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-center text-neutral-500 dark:text-slate-300 mt-5 font-medium">
                  Multilingual: 2 credits / character &middot; Flash models: 1 credit / character
                </p>
                <p className="text-xs text-center text-neutral-400 dark:text-slate-500">
                  You will be redirected to a secure Stripe checkout page.
                </p>
              </motion.div>
            )}

            {error && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300 text-sm font-semibold text-center mt-4">{error}</div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 mt-8">
              {step > 1 && (
                <button type="button" onClick={() => setStep(step - 1)} className="px-6 py-3 rounded-xl border border-neutral-300 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors">
                  {T("auth.back")}
                </button>
              )}
              <button type="submit" disabled={loading} className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-teal-500 text-white font-bold text-sm hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>{step === 2 ? (selectedPlan === "free_trial" ? "Start with Free Trial" : "Pay with Stripe") : T("auth.continue")} <ArrowRight size={16} /></>
                )}
              </button>
            </div>
          </form>
        </div>

        <p className="text-sm text-neutral-500 dark:text-slate-400 text-center mt-6 font-medium">
          {T("auth.has_account")}{" "}
          <Link href="/login" className="text-cyan-600 dark:text-cyan-300 font-bold hover:text-cyan-700 dark:hover:text-cyan-200 transition-colors">{T("auth.login")}</Link>
        </p>

        <div className="flex items-center justify-center mt-4 relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-1.5 text-xs text-neutral-400 dark:text-slate-500 hover:text-neutral-600 dark:hover:text-slate-300 font-medium transition-colors"
          >
            <Globe size={12} />
            {LANGS.find(l => l.code === lang)?.label}
            <ChevronDown size={10} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
          </button>
          {langOpen && (
            <div className="absolute bottom-full mb-1 bg-white dark:bg-slate-900 rounded-xl border border-neutral-200 dark:border-white/10 shadow-xl py-1 w-32 z-50">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setLangOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${lang === l.code ? "text-cyan-600 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-500/10" : "text-neutral-600 hover:bg-neutral-50 dark:text-slate-300 dark:hover:bg-white/5"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
